import {Render} from "./Render";
import {RouteLayer} from "@esWebAPI/es/map/layer/route/RouteLayer";

import type {AzureBaseMap} from "../AzureBaseMap";
import type {IEventChannel, InitMapParams} from "@esWebAPI/es/model/Models";
import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type { AzureOverlay } from "../AzureOverlay";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type Layer from "@esWebAPI/es/map/module/Layer";
import type {IRoutingModule} from "@esWebAPI/es/map/module/ScheduleModule";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import _ from "lodash";
import DirectionsModel from "@esWebAPI/es/map/DirectionsModel";
import {Service, StopInfo} from "@esWebAPI/es/model/Schedule";


export class DayTripRender extends Render {

    private readonly directionsModule: DirectionsModule;

    private layerModule: Layer;

    constructor(
        baseMap: AzureBaseMap,
        featureViewModel: any,
        directionModule: DirectionsModule,
        analysis: any,
        layerModule: Layer,
        initMapParams: InitMapParams,
        overlayModule: AzureOverlay,
        stylerFactory: ThematicStylerFactory,
        eventChannel: IEventChannel
    ) {
        super(baseMap, featureViewModel, analysis, initMapParams, overlayModule, stylerFactory, eventChannel);
        this.layerModule = layerModule;
        this.directionsModule = directionModule;
    }

    public startLoad() {
        this.directionsModule.getModel().getEventChannel().addOnce("onModelReady", () => {
            super.startLoad();
            this.init();
        });
    }

    private bindUpdate = _.debounce(this.update.bind(this), 600,{
        trailing: true
    });

    private init() {
        this.loadComplete.then(() => {
            this.directionsModule.getModel().getEventChannel().addListener("onModelChange", this.bindUpdate)
        });
    }


    async update() {

        const stops = this.directionsModule.getServicesObjRef();
        const layerViewIdsWithStops = _.uniq(stops.map((s:any) => s.layerViewId));
        if (layerViewIdsWithStops.length > 0 && layerViewIdsWithStops[0] == undefined){
            // Model is in an invalid state. A new updated will be triggered with the correct values;
            return;
        }

        const pointLayers = this.layerModule.list().filter((lv: LayerViewModel) => lv.getGeomType() === "POINT");

        for (const layer of pointLayers) {
            if (layer.isEnabled() === false) {
                // Legend-disabled layers are lazy-skipped at load and have no map layer -
                // don't retry-wait for one that will never appear.
                continue;
            }

            const mapLayer: RouteLayer = this.baseMap.getLayersByLayerViewId(layer.getId())[0] as RouteLayer;
            if (!mapLayer) {
                console.warn("There should be 1 map Layers for " + layer.getId(), mapLayer);
                setTimeout(this.bindUpdate, 500);
                return;
            }

        }

        super.reorderLayers();
    }

    protected async addSymbolLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        if(!this.directionsModule) {
            console.error("Directions model not available yet");
            return;
        }



        /*
        let unscheduledLayer;
        //Radius/DriveTime analysis need a special layer has it has GeometryCollection features (1 Point + 1 Polygon)
        if (this.analysis && ["WithinDistanceAnalysis", "DriveTimeAnalysis"].includes(this.analysis.$type) && this.analysis.generatedAggregateLayerViewId === layerView.id) {
            unscheduledLayer = new AzureSymbolAndPolygonLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(), this.spriteManager, this.eventChannel);
        } else {
            unscheduledLayer = new AzureHybridLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(), this.spriteManager, this.eventChannel);
        }
        this.hideStopPointAndClusters(unscheduledLayer);

        this.baseMap.addLayer(unscheduledLayer);
        //const scheduledLayer = new AzureScheduleSymbolStopLayer(this._map, this.directionsModule, layerView, layerInfo, this.baseMap.getDatasourceManager(), this.spriteManager, this.eventChannel);
        //this.baseMap.addLayer(scheduledLayer);
        */
        const layer = new RouteLayer(this._map, layerView, layerInfo,
            this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory, this.directionsModule, this.analysis, this.eventChannel);
        this.baseMap.addLayer(layer);
    }
}

// This is a Adapter/Mock class the creates a Module compatible with the AzureScheduleSumbolStopLayer's ScheduleModule
export class DirectionsModule implements IRoutingModule {

    private readonly model: DirectionsModel

    constructor(model: DirectionsModel) {
        this.model = model;
    }

    public isShiftActive() {
        return true;
    }

    private getStops(): Service[] {

        const getAddress= (geom: any) => {
            const g = geom[0] === "MG" ? geom[1] : geom;
            return {
                lon: g.slice(1)[0],
                lat: g.slice(1)[1],
            }
        };

        if(!this.model) {
            return []
        }

        let services: Service[] = [];
        if(this.model.isEditMode()){
            services = this.model.getStops().filter((s: any) => s.geom).map((s: any) => (
                {
                    layerViewId: s.layerViewId,
                    featureId: s.id,
                    address: getAddress(s.geom)
                } as Service));
        } else {
            const stops = this.model.getStops().filter((s:any) => s.geom);
            if(stops.length && !stops[0].featKey) {
                // edge case before the prepareModel has finished
                return [];
            }
            services = stops.map((s:any) => {
                const [layerViewId, featureId] = s.featKey.split(":").map((id:string) => +id);
                return {layerViewId, featureId, address: getAddress(s.geom)}  as Service;
            });
        }

        if(services.length === 0) {
            return [];
        }

        if(services.length === 1 || this.model.isRoundTrip()) {
            services[0].stopInfo = {type: "START_END"} as StopInfo;
            return services;
        }

        services[0].stopInfo = {type: "START"} as StopInfo;
        services[services.length-1].stopInfo = {type: "END"} as StopInfo;
        return services as Service[];
    }

    public getShiftColorServer(){
        const SHORT_LIST_COLOR = 4294554432;
        return SHORT_LIST_COLOR;
    }

    public getShortListColorServer() {
        const SHORT_LIST_COLOR = 4294554432;
        return SHORT_LIST_COLOR;
    }

    public getInactiveColorServer() {
        const SHORT_LIST_COLOR = 4294554432;
        return SHORT_LIST_COLOR;
    }

    public getInactiveColorHex(): string {
        return "";
    }

    public getShiftColorHex(_shiftId: string): string {
        return "";
    }

    public getServicesObjRef() {
        return this.getStops();
    }

    public getSettingsObjRef() {
        const SHORT_LIST_COLOR = 4294554432;
        return {
            colors: [SHORT_LIST_COLOR]
        }
    }

    public getEventChannel() {
        return this.model.getEventChannel()
    }

    public isServiceBreak() {
        return false;
    }

    public getShortListColorHex() {
        return es.util.color.serverToHex(4294554432);
    }

    public getModel() {
        return this.model;
    }

    util = {
        getServicesFromFeat: ({layerViewId, id}: {layerViewId: number, id: number}) => {
            return this.getStops()
                .filter(s => s.layerViewId === layerViewId && s.featureId === id)
        },
        getShiftFromService() {
            return undefined;
        },
        getStopNumberFromService: (service: Service) => {
            if(this.model.isEditMode() && this.model.isOptimized()) {
                return -1;
            }

            // services that have stops info are the START/END/STAT_END and they have a different icon and no text
            if(service.stopInfo) {
                return -1;
            }

            const stops = this.getStops();
            return _.findIndex(stops, s => s.featureId === service.featureId && s.layerViewId === service.layerViewId);
        },
        removeServicesFromFeat: (_feat: any) => {
        }
    }
}
